import { LicensePlateType } from 'dg-web-shared/dto/LicensePlateType.ts';
import { Store, useStore } from 'dg-web-shared/lib/Flux.tsx';
import { Response } from 'dg-web-shared/lib/HttpResponse.ts';
import {
    getOrElse,
    isDefined,
    Maybe,
    thenElse,
} from 'dg-web-shared/lib/MaybeV2.ts';
import * as Http from '../../api/Http.ts';
import * as AsyncRequest from '../../AsyncRequest.ts';
import {
    LicensePlateTrippleEntry,
    Tripple,
} from '../../common/components/LicensePlateTrippleEntry.tsx';
import { Translation } from '../../common/i18n/Text.ts';
import * as OperatorDataState from '../../common/state/OperatorDataState.ts';
import * as PermitTypeState from '../../common/state/PermitTypeState.ts';
import { ButtonSpecialState } from '../../ui/buttons/IconButton.tsx';
import {
    ConfirmationHeader,
    HalfSlideIn,
    SlideInBody,
} from '../../ui/slidein/Slidein.tsx';
import * as AddLicensePlateTexts from '../i18n/AddLicensePlateTexts.ts';
import * as LicensePlateState from '../state/LicensePlateState.ts';
import * as PermitCreateState from '../state/PermitCreateState.ts';
import * as PermitEditState from '../state/PermitEditState.ts';
import { useLanguage } from 'dg-web-shared/lib/Localization.tsx';
import {
    addLicensePlateToExistingPermit,
    addLicensePlateToNewPermit,
} from './PermitLicensePlateSlidein.tsx';

export interface LicensePlate {
    id: number;
    licensePlateNr: string;
    type: LicensePlateType;
    country: string;
}

export interface Texts {
    HeaderCaption: Translation;
    HeaderHeading: Translation;
    LicensePlateType: Translation;
    Country: Translation;
    LicensePlate: Translation;
    CarType: Translation;
    MotorcycleType: Translation;
    AlreadySelectedLicensePlate: Translation;
}

enum DisplayMode {
    CREATE,
    EDIT,
}

const closeSlideIn = (store: Store): string => {
    LicensePlateState.AddTripple.reset(store);
    return 'AddLicensePlateSlideIn-cancel';
};

const sendTripple = (needsVignette: boolean, carDescription: boolean) =>
    AsyncRequest.request(
        Http.OperatorAccount.LicensePlates.postTripple,
        (store: Store, res: Response): string => {
            // let's set the response on the add server data
            LicensePlateState.AddServerData.setResponse(store, res);
            if (res.statusCode.cls.success) {
                // on success, set the data also to the ServerData to avoid another
                // request
                const successData =
                    LicensePlateState.AddServerData.get(store).successData;

                const createEnabled =
                    PermitCreateState.Layout.get(store).createEnabled;
                if (successData) {
                    if (createEnabled) {
                        LicensePlateState.Entity.new_permit_add(
                            store,
                            successData.id,
                        );
                        if (!needsVignette && !carDescription) {
                            store.update(store =>
                                addLicensePlateToNewPermit(store, successData),
                            );
                        }
                    } else {
                        LicensePlateState.Entity.existing_permit_add(
                            store,
                            successData.id,
                        );
                        if (!needsVignette && !carDescription) {
                            store.update(store =>
                                addLicensePlateToExistingPermit(
                                    store,
                                    successData,
                                ),
                            );
                        }
                    }
                }
                // hide current slidein
                closeSlideIn(store);
            }
            return 'AddLicensePlateSlideIn-sendTripple';
        },
    );

export function AddLicensePlateSlideIn() {
    const { storeState, update } = useStore(store => {
        const permitCreateStateConfig = PermitCreateState.Config.get(store);
        const permitEditStateConfig = PermitEditState.Edit.get(store);

        const displayMode = isDefined(permitCreateStateConfig.permitTypeId)
            ? DisplayMode.CREATE
            : DisplayMode.EDIT;

        let permitTypeId: Maybe<number>;
        let selectedLicensePlates: LicensePlate[];
        if (displayMode === DisplayMode.CREATE) {
            permitTypeId = permitCreateStateConfig.permitTypeId;
            selectedLicensePlates = permitCreateStateConfig.licensePlates;
        } else {
            const permitData: Maybe<PermitEditState.Permit> =
                PermitEditState.Server.get(store).data;
            permitTypeId = thenElse(permitData, d => d.permitTypeId, null);
            const permitLicensePlates: LicensePlate[] = thenElse(
                permitData,
                permit => permit.licensePlates,
                [],
            );

            // after edits, the selected plates will be in the EditStateConfig
            selectedLicensePlates = getOrElse(
                permitEditStateConfig.licensePlates,
                permitLicensePlates,
            );
        }
        const permitType: Maybe<PermitTypeState.PermitType> =
            PermitTypeState.getById(store, permitTypeId);

        return {
            operatorData: OperatorDataState.get(store),
            addLp: LicensePlateState.AddTripple.get(store),
            relevantPermitType: permitType,
            addLpServer: LicensePlateState.AddServerData.get(store),
            selectedLicensePlates: selectedLicensePlates,
        };
    });
    const { language } = useLanguage();

    function txt(): Texts {
        return AddLicensePlateTexts.texts[language];
    }

    function renderContent() {
        const permitType = storeState.relevantPermitType;
        if (isDefined(permitType)) {
            return (
                <SlideInBody>
                    <LicensePlateTrippleEntry
                        language={language}
                        allowedLicensePlateTypes={permitType.allowedLicensePlateTypes.map(
                            t => t.type,
                        )}
                        onCountrySlideInToggle={() =>
                            update(store =>
                                LicensePlateState.AddTripple.stateWrite(store, {
                                    lpCountrySelectionOpen: true,
                                }),
                            )
                        }
                        onTypeSelect={(type: LicensePlateType) =>
                            update(store =>
                                LicensePlateState.AddTripple.stateWrite(store, {
                                    type,
                                }),
                            )
                        }
                        licensePlateType={storeState.addLp.type}
                        country={storeState.addLp.country}
                        licensePlateNr={storeState.addLp.licensePlateNr}
                        onLicensePlateNumberChange={(
                            v: string,
                            formValid: boolean,
                        ) => {
                            update(store =>
                                LicensePlateState.AddTripple.stateWrite(store, {
                                    licensePlateNr: v,
                                    formValid,
                                }),
                            );
                        }}
                        showErrors={storeState.addLp.confirmPressedWhileError}
                        outsideLicensePlateNrErrorGen={wasPlateAlreadySelectedGen()}
                    />
                </SlideInBody>
            );
        } else {
            return null;
        }
    }

    function buttonSpecialState(): Maybe<ButtonSpecialState> {
        if (
            storeState.addLp.confirmPressedWhileError &&
            !storeState.addLp.formValid
        ) {
            return ButtonSpecialState.ERROR;
        } else if (!storeState.addLp.formValid) {
            return ButtonSpecialState.DISABLED;
        } else {
            return null;
        }
    }

    function wasPlateAlreadySelectedGen(): (args: Tripple) => string | null {
        return (args: Tripple): string | null => {
            const { licensePlateNr, country, licensePlateType } = args;
            const wasSelected = storeState.selectedLicensePlates.some(plate => {
                return (
                    plate.country === country &&
                    plate.type === licensePlateType &&
                    plate.licensePlateNr === licensePlateNr
                );
            });
            if (wasSelected) {
                return txt().AlreadySelectedLicensePlate();
            } else {
                return null;
            }
        };
    }

    const lp = storeState.addLp;

    return (
        <HalfSlideIn open={storeState.addLp.createLpOpen} outsideBody>
            <ConfirmationHeader
                language={language}
                title={txt().HeaderCaption()}
                onCancel={() => update(closeSlideIn)}
                confirmButtonSpecialState={buttonSpecialState()}
                onConfirm={() => {
                    const permitType = storeState.relevantPermitType;
                    const needsVignette =
                        !isDefined(permitType) ||
                        permitType.allowedLicensePlateTypes.some(
                            lp => lp.needsVignette,
                        );
                    const carType =
                        (storeState.operatorData.data &&
                            storeState.operatorData.data.settings
                                .carTypeDescription) ||
                        false;

                    if (!storeState.addLp.formValid) {
                        update(store =>
                            LicensePlateState.AddTripple.stateWrite(store, {
                                confirmPressedWhileError: true,
                            }),
                        );
                    } else {
                        update(sendTripple(needsVignette, carType), {
                            licensePlateNr: lp.licensePlateNr as string,
                            type: lp.type as LicensePlateType,
                            country: lp.country as string,
                        });
                    }
                }}
            />
            {renderContent()}
        </HalfSlideIn>
    );
}
